import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  ArrowRight,
  Sparkles,
  X,
  Download,
  Mic,
  Users,
  Heart,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * High-impact Hero banner promoting the "Plan de Protección Preventiva".
 * Clicking "Ver cómo funciona" opens an educational popup describing the
 * I-589 assistance benefits, with a final "VER PRECIOS" CTA.
 */
export default function PreventivePlanHeroBanner() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        data-testid="landing-preventive-plan-banner"
        onClick={() => setShowModal(true)}
        className="group block w-full text-left rounded-2xl bg-gradient-to-br from-indigo-900 via-blue-900 to-indigo-700 text-white p-5 sm:p-6 shadow-[0_15px_40px_rgba(30,58,138,0.35)] relative overflow-hidden border-2 border-blue-400/30 mb-8 transition-transform hover:scale-[1.01]"
      >
        {/* Decorative glow */}
        <div className="absolute -top-12 -right-12 w-44 h-44 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-emerald-400/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-amber-400 text-blue-950 flex items-center justify-center shadow-lg">
            <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.5} />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[10px] sm:text-xs uppercase tracking-widest font-black text-amber-300 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Nuevo · Recomendado por miapoyo
            </p>
            <h2 className="text-xl sm:text-2xl font-black tracking-tight mb-1.5 leading-tight">
              Plan de Protección Preventiva
            </h2>
            <p className="text-sm sm:text-base font-medium text-blue-100 leading-snug mb-3">
              Prepara tu Formulario <span className="font-black text-white">I-589</span> con tu familia <span className="font-black text-amber-300">ANTES</span> de cualquier encuentro con ICE. Es tu mejor escudo legal.
            </p>

            <div className="hidden sm:flex flex-wrap gap-x-4 gap-y-1 mb-3">
              <Pill text="Evita deportación expedita" />
              <Pill text="Acelera tu fianza" />
              <Pill text="Blindaje contra el pánico" />
            </div>

            <div className="inline-flex items-center gap-2 text-amber-300 font-black text-sm sm:text-base group-hover:gap-3 transition-all">
              Ver cómo funciona
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={3} />
            </div>
          </div>

          <FileText className="hidden sm:block absolute -bottom-3 -right-3 w-28 h-28 text-white/5" strokeWidth={1} />
        </div>
      </button>

      {showModal && (
        <HowItWorksModal
          onClose={() => setShowModal(false)}
          onSeePricing={() => {
            setShowModal(false);
            navigate("/precios");
          }}
        />
      )}
    </>
  );
}

function Pill({ text }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-100">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
      {text}
    </span>
  );
}

function HowItWorksModal({ onClose, onSeePricing }) {
  return (
    <div
      data-testid="preventive-howitworks-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-800 text-white p-6 pr-14 flex-shrink-0">
          <button
            onClick={onClose}
            data-testid="preventive-howitworks-close"
            className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-400 text-indigo-950 flex items-center justify-center shadow-lg">
              <ShieldCheck className="w-7 h-7" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-black text-amber-300 mb-1 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" />
                Plan de Protección Preventiva
              </p>
              <h2 className="text-2xl font-black tracking-tight leading-tight">
                Te auxiliamos a descargar y llenar tu Formulario <span className="text-amber-300">I-589</span>
              </h2>
              <p className="text-sm text-blue-100 mt-2 leading-snug">
                Solicitud de Asilo. Es el documento que demuestra ante el gobierno de EE.UU. que tienes miedo creíble de regresar a tu país. Llenarlo <span className="font-black text-white">ANTES</span> de un encuentro con ICE puede salvarte de la deportación expedita.
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable benefits */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* What we do */}
          <section>
            <h3 className="text-[11px] font-black uppercase tracking-widest text-indigo-700 mb-3">
              ¿Qué hace nuestro servicio por ti?
            </h3>
            <div className="space-y-3">
              <BenefitRow
                Icon={Download}
                color="indigo"
                title="Te entregamos el formulario oficial"
                desc="Recibes el enlace oficial del USCIS al I-589 más el glosario de miapoyo. Lo descargas e imprimes desde tu casa con tu familia."
              />
              <BenefitRow
                Icon={Mic}
                color="emerald"
                title="Te guiamos casilla por casilla con voz IA"
                desc="Llamas a nuestra IA en español. Ella te dicta exactamente qué escribir en inglés, en MAYÚSCULAS, y deletrea cada palabra letra por letra. No necesitas saber inglés."
              />
              <BenefitRow
                Icon={Users}
                color="rose"
                title="Toda tu familia queda protegida"
                desc="Con el Plan Familiar (hasta 4 personas) puedes preparar el I-589 de tu cónyuge e hijos. Cada uno con su propio PIN y llamadas dedicadas."
              />
              <BenefitRow
                Icon={Heart}
                color="amber"
                title="Construido sobre manuales oficiales"
                desc="Nuestra IA fue entrenada con los manuales de auto-representación del Florence Project y NIJC. Lo mismo que usan los abogados pro bono."
              />
            </div>
          </section>

          {/* Why it matters */}
          <section className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <h3 className="text-sm font-black text-amber-900">
                ¿Por qué tener tu I-589 listo ANTES?
              </h3>
            </div>
            <ul className="space-y-2 text-xs text-amber-950 leading-snug">
              <BulletPoint>
                <span className="font-black">Evita deportación expedita.</span> Tener el formulario en mano fuerza al oficial a transferirte al proceso normal de corte de inmigración (no expedito).
              </BulletPoint>
              <BulletPoint>
                <span className="font-black">Acelera tu fianza.</span> El juez ve un caso preparado, no improvisado. Las fianzas suelen reducirse drásticamente.
              </BulletPoint>
              <BulletPoint>
                <span className="font-black">Te protege del pánico.</span> En el momento del arresto no tienes que recordar nada — tu familia entrega el formulario por ti.
              </BulletPoint>
              <BulletPoint>
                <span className="font-black">Tu familia sabe exactamente qué hacer.</span> Nuestra IA les enseña cuándo firmar, cuándo entregar, y a quién llamar primero.
              </BulletPoint>
            </ul>
          </section>

          {/* What you take home */}
          <section className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4">
            <h3 className="text-sm font-black text-emerald-900 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              Al terminar tendrás en tus manos:
            </h3>
            <ul className="space-y-1.5 text-xs text-emerald-950">
              <CheckLine>Tu I-589 lleno, impreso, listo para usar</CheckLine>
              <CheckLine>Sin firmar y sin fecha (regla crítica que enseñamos)</CheckLine>
              <CheckLine>Carpeta de evidencias organizada con tu familia</CheckLine>
              <CheckLine>Tu PIN único de acceso 24/7 para emergencias</CheckLine>
              <CheckLine>Resumen de tus llamadas guardado por 30 días</CheckLine>
            </ul>
          </section>

          {/* Legal disclaimer */}
          <p className="text-[11px] text-zinc-500 leading-snug px-1">
            <span className="font-bold">Aviso legal:</span> miapoyo es un asistente automatizado de educación legal. No somos abogados y esto no es asesoría legal. El I-589 lo llenas tú con nuestra guía paso a paso.
          </p>
        </div>

        {/* Sticky CTA footer */}
        <div className="border-t border-zinc-200 bg-white p-4 sm:p-5 flex-shrink-0">
          <Button
            data-testid="preventive-howitworks-see-pricing"
            onClick={onSeePricing}
            className="w-full h-14 text-base font-black bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl shadow-lg shadow-amber-500/30 flex items-center justify-center gap-2"
          >
            Ver precios
            <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </Button>
          <p className="text-center text-[11px] text-zinc-500 mt-2 leading-snug">
            Planes desde <span className="font-bold text-zinc-700">$49 USD</span> · Pago único · Sin suscripciones
          </p>
        </div>
      </div>
    </div>
  );
}

const COLOR_BG = {
  indigo: "bg-indigo-100 text-indigo-700",
  emerald: "bg-emerald-100 text-emerald-700",
  rose: "bg-rose-100 text-rose-700",
  amber: "bg-amber-100 text-amber-700",
};

function BenefitRow({ Icon, color, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${COLOR_BG[color] || COLOR_BG.indigo}`}>
        <Icon className="w-5 h-5" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-black text-zinc-900 leading-tight">{title}</p>
        <p className="text-xs text-zinc-600 leading-snug mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

function BulletPoint({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-600 mt-1.5" />
      <span>{children}</span>
    </li>
  );
}

function CheckLine({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}
